// ============================================================
// clientSelection.js — Platform-05, the client switcher
//
// A human can hold active ClientUsers rows under more than one
// Client (the live case: a Supporting Care franchise director
// spanning SC-Tower Hamlets / SC-Redbridge / SC-Barking / Finch
// Care — four Clients under ONE Tenant). This module owns the
// browser side of "which one am I working in".
//
// The selected ClientID rides on every authenticated request in
// the X-CareHub-Client-Id header (added in utils/api.js
// buildHeaders, the single choke point). withAuth validates it
// server-side against the rows it already resolved for the
// caller's verified oid, so this value can never widen access —
// the worst it can do is name a Client the caller does not hold,
// which the gate answers with 403 client_not_permitted.
//
// SESSION storage, not local (D-5). Per-tab is a genuine feature
// here: a franchise director can hold Tower Hamlets in one tab and
// Redbridge in another and compare them. localStorage would make
// the two tabs fight over one value. The accepted trade-off is
// that a newly opened tab starts on the default Client.
//
// Every access is try/catch-wrapped so Safari private mode (which
// can throw on both getItem and setItem) degrades silently to "no
// selection" — i.e. exactly the pre-Platform-05 behaviour — rather
// than throwing into the UI. Mirrors utils/uiPreferences.js.
// ============================================================

const STORAGE_KEY = 'a2zcarehub.session.clientId';

// Must match CLIENT_SELECTION_HEADER in the backend's
// shared/client-selection.js, and the APIM CORS allowed-headers
// entry. Deliberately NOT the retired X-Client-Id.
export const CLIENT_SELECTION_HEADER = 'X-CareHub-Client-Id';

/**
 * The ClientID this tab is working in, or null for "no preference"
 * (the server then binds its default row).
 *
 * @returns {number|null}
 */
export function getSelectedClientId() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined || raw === '') return null;
    const value = Number(raw);
    return Number.isSafeInteger(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

/**
 * The selected-client header for an authenticated request, or {} when no
 * client is selected (every single-client user). EVERY authenticated fetch in
 * the SPA spreads this next to its Authorization header: utils/api.js
 * buildHeaders and each *-api.js module's own fetch helper. Until 2026-10-28
 * only buildHeaders sent it, so after a switch every module screen (rota,
 * care plan, HR, governance) silently read the person's DEFAULT client while
 * the header showed the switched one. Lives here, not in api.js, so the many
 * tests that mock api.js keep working.
 * __tests__/clientSelectionHeaderEverywhere fails any Bearer header that does
 * not spread this.
 *
 * @returns {Object<string,string>}
 */
export function selectedClientHeaders() {
  const selectedClientId = getSelectedClientId();
  return selectedClientId !== null ? { [CLIENT_SELECTION_HEADER]: String(selectedClientId) } : {};
}

export function setSelectedClientId(clientId) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(clientId));
  } catch {
    // Storage blocked. The switch will not survive the reload, so
    // the user simply stays where they are — safe, if inert.
  }
}

export function clearSelectedClientId() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
}

/**
 * Drop a stored selection that the server no longer offers.
 *
 * Called on every bootstrap against me/entitlements.availableClients.
 * Without it a stale value (access revoked, row deactivated, a
 * hand-edited storage entry) would 403 EVERY request and lock the
 * tab out of the app entirely — the server fails closed by design,
 * so the client has to be the thing that recovers.
 *
 * @param {Array<{clientId: number}>} availableClients
 * @returns {boolean} true if a stale selection was cleared.
 */
export function reconcileSelection(availableClients) {
  const selected = getSelectedClientId();
  if (selected === null) return false;

  const list = Array.isArray(availableClients) ? availableClients : [];
  // An absent/empty list means the endpoint could not tell us (it
  // degrades rather than failing). Treat that as "unknown", not as
  // "you hold nothing" — clearing here would silently bounce a
  // switched user back to their default Client on a transient blip.
  if (list.length === 0) return false;

  if (list.some((c) => Number(c.clientId) === selected)) return false;

  clearSelectedClientId();
  return true;
}

/**
 * Is there more than one Client to choose between? The switcher
 * surfaces render only when this is true, so a single-client user
 * (every live human but one) sees nothing new anywhere.
 *
 * @param {Array} availableClients
 * @returns {boolean}
 */
export function hasMultipleClients(availableClients) {
  return Array.isArray(availableClients) && availableClients.length > 1;
}

/**
 * Switch this tab to another Client (D-4).
 *
 * A FULL re-bootstrap via hard navigation, deliberately — NOT a
 * refetch and NOT a React state update. RoleKey lives on the
 * ClientUsers row, so it is per-Client: the live multi-client human
 * is a manager in one Client and a carer in another. Switching can
 * therefore change which SHELL renders, which modules resolve and
 * which routes exist. Unmounting one shell tree and mounting another
 * while stale route state and in-flight fetches are still live is
 * where the subtle bugs breed; a hard navigation costs a few hundred
 * milliseconds and is correct by construction.
 *
 * Landing on the shell root rather than the current path is
 * deliberate too: the current route may not exist for the role the
 * user is about to become.
 *
 * @param {number} clientId
 * @param {string} basePath '/app' (browser) or '/teams'.
 */
export function switchToClient(clientId, basePath = '/app') {
  setSelectedClientId(clientId);
  window.location.assign(basePath);
}
